import { inject, Injectable, NgZone, signal } from '@angular/core';
import { publishNotificationSession } from 'sham-components/services/notification-session';
import { HttpClient } from '@angular/common/http';
import {
  catchError,
  finalize,
  Observable,
  shareReplay,
  Subject,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import { API_BASE_URL } from '@/environments/environment';
import { Router } from '@angular/router';
import {
  LoginResponseDto,
  SigninSubmitRequest,
  UserDto,
} from 'sham-components/shared/models/auth';
import {
  clearDashboardSessionStorage,
  verifyDashboardSession,
} from 'sham-components/services';

type AuthUser = UserDto & {
  profilePhotoUrl?: string | null;
  avatarUrl?: string | null;
  photoUrl?: string | null;
  imageUrl?: string | null;
  profileImageUrl?: string | null;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly AUTH_URL = `${API_BASE_URL}/api/v1/auth`;

  readonly isLoggedIn = signal<boolean>(!!sessionStorage.getItem('accessToken'));
  readonly currentUser = signal<AuthUser | null>(this.readStoredUser());
  private refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private refreshRequest$: Observable<LoginResponseDto> | null = null;
  private readonly logout$ = new Subject<void>();

  constructor() {
    if (this.isLoggedIn()) {
      this.queueSilentRefresh();
    }
  }

  processRefreshVerification(): Observable<LoginResponseDto | boolean | null> {
    return verifyDashboardSession({
      refresh: () => this.refreshToken(),
      onValidAccess: () => {
        this.isLoggedIn.set(true);
        this.queueSilentRefresh();
      },
      clear: () => this.clearSession(),
    });
  }

  login(credentials: SigninSubmitRequest): Observable<LoginResponseDto> {
    return this.http.post<LoginResponseDto>(`${this.AUTH_URL}/login`, credentials);
  }

  saveLogin(res: LoginResponseDto, rememberMe: boolean) {
    sessionStorage.setItem('accessToken', res.accessToken);
    sessionStorage.setItem('accessTokenExpiresAt', res.accessTokenExpiresAtUtc);
    sessionStorage.setItem('user', JSON.stringify(res.user));
    localStorage.removeItem('refreshToken');
    sessionStorage.removeItem('refreshToken');
    if (res.refreshToken) {
      (rememberMe ? localStorage : sessionStorage).setItem(
        'refreshToken',
        res.refreshToken,
      );
    }

    this.currentUser.set(res.user as AuthUser);
    this.isLoggedIn.set(true);
    this.queueSilentRefresh();
  }

  refreshToken(): Observable<LoginResponseDto> {
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    const refreshToken =
      localStorage.getItem('refreshToken') ?? sessionStorage.getItem('refreshToken');
    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error('No token'));
    }

    const rememberMe = localStorage.getItem('refreshToken') !== null;

    this.refreshRequest$ = this.http
      .post<LoginResponseDto>(`${this.AUTH_URL}/refresh-token`, { refreshToken })
      .pipe(
        takeUntil(this.logout$),
        tap((res) => this.saveLogin(res, rememberMe)),
        catchError((err) => {
          this.clearSession();
          return throwError(() => err);
        }),
        finalize(() => (this.refreshRequest$ = null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    return this.refreshRequest$;
  }

  logout() {
    this.logout$.next();
    this.clearSession();
    void this.router.navigate(['/login']);
  }

  private clearSession() {
    if (this.refreshTimeoutId) clearTimeout(this.refreshTimeoutId);
    clearDashboardSessionStorage();
    this.currentUser.set(null);
    this.isLoggedIn.set(false);
  }

  isSeller(): boolean {
    const user = this.getUser();
    return user?.roles.includes('Seller') ?? false;
  }

  isCustomer(): boolean {
    const user = this.getUser();
    return user?.roles.includes('Customer') ?? false;
  }

  isDriverAdmin(): boolean {
    return this.getUser()?.roles.includes('DriverAdmin') ?? false;
  }

  getUser(): AuthUser | null {
    return this.currentUser();
  }

  handleLoginRedirection() {
    void this.router.navigate(['/app']);
  }

  private queueSilentRefresh() {
    publishNotificationSession();
    const expiresAt = sessionStorage.getItem('accessTokenExpiresAt');
    if (!expiresAt) {
      return;
    }

    const delay = new Date(expiresAt).getTime() - Date.now() - 2 * 60 * 1000;
    if (this.refreshTimeoutId) {
      clearTimeout(this.refreshTimeoutId);
    }

    this.zone.runOutsideAngular(() => {
      this.refreshTimeoutId = setTimeout(
        () => {
          this.zone.run(() => this.refreshToken().subscribe());
        },
        Math.max(0, delay),
      );
    });
  }

  private readStoredUser(): AuthUser | null {
    const userStr = sessionStorage.getItem('user');
    if (!userStr) {
      return null;
    }

    try {
      return JSON.parse(userStr) as AuthUser;
    } catch {
      return null;
    }
  }
}
